import { Equipment, resolveKnownBrand } from './plan-detail';

/** Synthetic `deviceId` prefix for rows that stand in for a covered-but-unregistered certificate. */
export const SERVICE_REQUEST_PLACEHOLDER_ID_PREFIX = 'placeholder:';

/**
 * One selectable product row in the service-request "pick your unit" step.
 *
 * Before multi-policy support that step rendered raw `Equipment[]` straight off the single
 * selected plan, so the owning plan was implicit in every downstream call. Now that products are
 * aggregated across every active policy, each row carries its own plan/certificate context — the
 * GSSP handover and the add/update-equipment calls are all plan-scoped and must target the policy
 * that actually covers the chosen product, not whatever happens to be in `$selectedPlan`.
 *
 * Deliberately EXTENDS `Equipment` rather than wrapping it, so the existing HVAC and appliance
 * templates keep binding `unit.model` / `appliance.manufacturerName` unchanged.
 */
export interface IServiceRequestProduct extends Equipment {
  /** Plan number of the policy that covers this product. */
  planNumber: string;
  /** Dealer code of that same policy — required by the GSSP token handover. */
  dealerCode: string;
  /** Certificate covering this product. For a placeholder, the certificate whose
   * `allowedDeviceTypes` matched the chosen device type. */
  certificateNumber: string;
  /** The covering certificate's `product.productDescription`, used as the card's fallback
   * description when the equipment itself carries none (always the case for placeholders). */
  certificateProductDescription: string;
  /** True when the owning policy is a single-certificate-item policy (`isMultiCertPolicy !== true`).
   * Only these produce placeholder rows — a multi-cert policy with no equipment has nothing to
   * register the customer's details against. */
  isSingleCertPolicy: boolean;
  /** True while this row stands in for a certificate that covers the device type but has no
   * equipment registered against it. Flips to false once the customer's details are saved. */
  isPlaceholder: boolean;
}

/**
 * Whether a product still needs details from the customer before a claim can be started.
 *
 * Mirrors the dashboard's `FindYourCoverageComponent.isProductIncomplete` so the two surfaces
 * agree on what "missing info" means: no registered equipment at all, or equipment missing any
 * of the four fields the certificate-item-info form collects.
 */
export function needsProductInfo(product: IServiceRequestProduct): boolean {
  return product.isPlaceholder || isEquipmentInfoIncomplete(product);
}

/** Shared "missing info" predicate — see {@link needsProductInfo}. */
export function isEquipmentInfoIncomplete(equipment: Equipment | null | undefined): boolean {
  if (!equipment) {
    return true;
  }
  return !equipment.deviceTypeCode || !resolveKnownBrand(equipment.manufacturerName) || !equipment.model || !equipment.purchaseDate || equipment.purchasePrice < 2;
}
